import { useTranslation } from "@/hooks";
import type { PaymentRow } from "@/pages/payment/index/components/types";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import { getPaymentStateTranslation } from "@/utils/translations";
import {
	Chip,
	type ChipVariant,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Icon,
	Typography,
} from "@st-marketplace/ui";
import type { FC, ReactNode } from "react";

interface PaymentDetailsDialogProps {
	payment: PaymentRow;
	open: boolean;
	onOpenChange: (value: boolean) => void;
}

const STATUS_VARIANT: Record<PaymentRow["state"], ChipVariant> = {
	pending: "warning",
	accepted: "success",
	rejected: "error",
};

const Detail = ({
	label,
	children,
}: { label: string; children: ReactNode }) => (
	<div className="min-w-0 rounded-lg border border-secondary-border bg-background-50 p-3">
		<dt className="text-secondary-text-100 text-xs">{label}</dt>
		<dd className="mt-1 break-words text-sm">{children}</dd>
	</div>
);

const PaymentDetailsDialog: FC<PaymentDetailsDialogProps> = ({
	payment,
	open,
	onOpenChange,
}) => {
	const { t, lang } = useTranslation();
	const status = getPaymentStateTranslation(payment.state, t);
	const amount = formatNumber(payment.amount ?? 0, "price");
	const note = "payment_note" in payment ? payment.payment_note : null;
	const isAutomatic =
		payment.type === "MARKET_AUTOMATIC" ||
		payment.type === "SYSTEM_AUTOMATIC" ||
		payment.type === "SUPPLIER_AUTOMATIC";
	const isManual =
		payment.type === "MARKET_MANUAL" ||
		payment.type === "SYSTEM_MANUAL" ||
		payment.type === "SUPPLIER_MANUAL";

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-full max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-3">
						<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-fill/10">
							<Icon name="receipt-line" className="text-primary-100" />
						</span>
						{t("payment.details.title")}
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-4 px-4 pb-2">
					<div className="flex flex-col gap-3 rounded-xl border border-secondary-border bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<Typography variant="caption" className="text-secondary-text-100">
								{t("payment.paid_amount")}
							</Typography>
							<Typography variant="title-lg" className="mt-1 tabular-nums">
								{amount} {t("payment.form.eur")}
							</Typography>
						</div>
						<Chip variant={STATUS_VARIANT[payment.state]} size="sm">
							{status.text}
						</Chip>
					</div>

					<dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<Detail label={t("payment.payment_method")}>
							{isManual
								? t(`payment.form.method.${payment.manual_payment_method}`)
								: t(`payment.type.${payment.type}`)}
						</Detail>
						<Detail label={t("payment.created_at")}>
							<time dateTime={payment.created_at}>
								{getTimeSinceText(payment.created_at, t, lang)}
							</time>
						</Detail>

						{"buyer_name" in payment && (
							<Detail label={t("relation.addRequest.buyer")}>
								<RelationNameLink
									name={payment.buyer_name}
									relationId={payment.relation_id}
									party="buyer"
								/>
							</Detail>
						)}
						{"seller_name" in payment && (
							<Detail label={t("relation.addRequest.seller")}>
								{"relation_id" in payment ? (
									<RelationNameLink
										name={payment.seller_name}
										relationId={payment.relation_id}
										party="seller"
									/>
								) : (
									payment.seller_name
								)}
							</Detail>
						)}
						{"creator_name" in payment && (
							<Detail label={t("payment.adjustment.creator")}>
								{payment.creator_name}
							</Detail>
						)}

						{isAutomatic && (
							<>
								<Detail label={t("payment.details.usdtAmount")}>
									<span className="tabular-nums">
										{formatNumber(payment.claimed_amount, "price")} USDT
									</span>
								</Detail>
								<Detail label={t("payment.details.network")}>
									{payment.network}
								</Detail>
								<Detail label={t("payment.details.wallet")}>
									<span className="break-all font-mono text-xs" dir="ltr">
										{"seller_wallet_address" in payment
											? payment.seller_wallet_address
											: "supplier_wallet_address" in payment
												? payment.supplier_wallet_address
												: payment.system_wallet_address}
									</span>
								</Detail>
								<Detail label={t("payment.details.transactionId")}>
									<span className="break-all font-mono text-xs" dir="ltr">
										{payment.transaction_id ?? "—"}
									</span>
								</Detail>
							</>
						)}

						{note && (
							<div className="sm:col-span-2">
								<Detail label={t("payment.form.note")}>{note}</Detail>
							</div>
						)}
					</dl>
				</div>

				<DialogFooter
					buttons={{
						secondary: {
							label: t("common.button.close"),
							props: {
								type: "button",
								onClick: () => onOpenChange(false),
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

export default PaymentDetailsDialog;
